import {Timestamp} from 'firebase/firestore';
import type {ContentSample, ContentTag} from '../services/bugSynthesisService';

/**
 * Entite bug : le point de convergence des deux sources de detection.
 *
 * Un bug peut naitre d'une issue Sentry (preuve technique, mais rien ne dit
 * qu'elle gene), d'un ou plusieurs feedbacks (le vecu, mais rien ne prouve la
 * panne), ou des deux. C'est cette entite qui empeche le meme bug d'exister en
 * double — une fois comme feedback traite, une fois comme issue Sentry resolue —
 * et c'est elle qui sait qui prevenir au moment de livrer le correctif.
 */

export type BugStatus =
    | 'detected'        // repere, pas encore confirme
    | 'verified'        // confirme comme vrai bug
    | 'not_reproduced'  // investigue, pas reproduit (terminal)
    | 'wont_fix'        // decide de ne pas corriger (terminal)
    | 'fixed'           // correctif merge, pas encore livre
    | 'shipped';        // livre dans une version publiee

/** D'ou vient la detection initiale. Ne change plus apres la creation. */
export type BugOrigin = 'sentry' | 'feedback' | 'manual';

export type BugSeverity = 'critical' | 'major' | 'minor';

export interface Bug {
    id: string;
    title: string;
    status: BugStatus;
    origin: BugOrigin;
    severity: BugSeverity;
    /** Pourquoi c'est consideré comme un bug. Repris du candidat a la promotion. */
    rationale?: string;
    /** false quand l'app a detecte la situation et a continue : l'incident
     *  compte des utilisateurs sans les avoir genes. Absent quand rien ne
     *  permet de trancher — on ne prejuge alors pas de l'absence de gene. */
    impactsUsers?: boolean;
    /** Commentaire libre de l'equipe, saisi depuis la liste. Un seul par bug :
     *  ce qu'il faut savoir maintenant, pas l'historique de ce qui a ete dit. */
    note?: string;

    // ─── Preuve technique (Sentry) ──────────────────────────────────────────
    /** Id numerique Sentry. Sert de cle de deduplication : deux bugs ne
     *  doivent jamais porter le meme. Firestore n'ayant pas de contrainte
     *  d'unicite, c'est createBugFromCandidate qui la garantit. */
    sentryIssueId?: string;
    sentryShortId?: string;   // ex. FLUTTER-3SM
    sentryUrl?: string;
    sentryCulprit?: string;
    /** Instantanes au moment du rattachement, pas des valeurs vivantes. */
    eventCount?: number;
    userCount?: number;
    firstSeenAt?: Timestamp;
    lastSeenAt?: Timestamp;
    /** Dernier signalement recu, distinct de lastSeenAt : un bug ne venant que
     *  de signalements n'a pas d'issue Sentry pour dater sa derniere
     *  manifestation. */
    lastReportedAt?: Timestamp;
    /** Quel contenu est en cause, fige a la promotion : la fiche doit le dire
     *  sans rouvrir Sentry. `contentSample` ne porte qu'un evenement, faute de
     *  tags indexes sur les versions livrees. */
    contentTags?: ContentTag[];
    contentSample?: ContentSample;

    // ─── Preuve d'usage (feedbacks) ─────────────────────────────────────────
    /** Peut etre vide : un bug detecte par Sentry seul n'a aucun signalant. */
    feedbackIds: string[];

    // ─── Cycle de vie ───────────────────────────────────────────────────────
    platforms?: string[];
    affectedVersions?: string[];
    /** Renseigne au passage en 'shipped'. Separe du statut pour que le
     *  vocabulaire de statuts ne grossisse pas d'une valeur par version. */
    fixVersion?: string;
    prUrl?: string;
    /** SHA du commit qui corrige. Redondant pour un bug portant une issue
     *  Sentry — la convention `Fixes FLUTTER-3T5` rend le commit trouvable par
     *  un simple grep. Indispensable pour un bug ne venant que d'un feedback :
     *  rien alors ne relie le correctif au signalement, et l'information se
     *  perd des que celui qui a corrige passe a autre chose. */
    fixCommit?: string;
    regressionTestRef?: string;

    // ─── Notification des signalants ────────────────────────────────────────
    /** Sous-ensemble de feedbackIds deja prevenu. Volontairement distinct :
     *  on ne notifie que les gens qui ont ecrit, pas tous les utilisateurs
     *  touches par l'issue Sentry. */
    notifiedFeedbackIds?: string[];
    notifiedAt?: Timestamp;

    createdAt: Timestamp;
    createdBy: string;
    updatedAt?: Timestamp;
    updatedBy?: string;
}

/**
 * Candidat propose par l'analyse IA, avant validation humaine.
 * N'est jamais persiste tel quel : l'admin le promeut en Bug, ou l'ignore.
 */
export interface BugCandidate {
    title: string;
    severity: BugSeverity;
    /** Une ou deux phrases : ce qui fait penser que c'est un vrai bug. */
    rationale: string;
    /** Voir Bug.impactsUsers : la valeur suit le candidat jusqu'a la fiche,
     *  sinon « Minor » y ressemblerait a un arbitrage plutot qu'a un constat. */
    impactsUsers?: boolean;
    /** Les signalements regroupes sous ce candidat. */
    feedbackIds: string[];
    /** Issues Sentry associees, par shortId (FLUTTER-3SM). */
    sentryShortIds: string[];
    platforms: string[];
    affectedVersions: string[];
    /** Date ISO du signalement le plus recent du groupe. Absente pour un
     *  candidat detecte depuis Sentry seul : c'est alors `sentry.lastSeen`. */
    lastReportedAt?: string | null;
}

export const BUG_STATUS_LABELS: Record<BugStatus, string> = {
    detected: 'Détecté',
    verified: 'Vérifié',
    not_reproduced: 'Non reproduit',
    wont_fix: 'Abandonné',
    fixed: 'Corrigé',
    shipped: 'Livré',
};

export const BUG_STATUS_COLORS: Record<BugStatus, string> = {
    detected: 'bg-gray-100 text-gray-700',
    verified: 'bg-orange-100 text-orange-700',
    not_reproduced: 'bg-gray-100 text-gray-500',
    wont_fix: 'bg-gray-100 text-gray-500',
    fixed: 'bg-blue-100 text-blue-700',
    shipped: 'bg-green-100 text-green-700',
};

export const BUG_SEVERITY_LABELS: Record<BugSeverity, string> = {
    critical: 'Critical',
    major: 'Major',
    minor: 'Minor',
};

export const BUG_SEVERITY_COLORS: Record<BugSeverity, string> = {
    critical: 'bg-red-100 text-red-700',
    major: 'bg-orange-100 text-orange-700',
    minor: 'bg-gray-100 text-gray-600',
};

/** Statuts terminaux : plus rien n'est attendu sur ce bug. */
export const BUG_TERMINAL_STATUSES: BugStatus[] = ['not_reproduced', 'wont_fix', 'shipped'];
